import { lazy, Suspense, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { AnalyticsObserver } from './analytics/AnalyticsObserver';
import type { SeoDefinition } from './calculators/types';
import { ProductSeo } from './components/ProductSeo';
import { AppLayout } from './layouts/AppLayout';
import { CalculatorPage } from './pages/CalculatorPage';
import { DecisionStudioPage } from './pages/DecisionStudioPage';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProductHomePage } from './pages/ProductHomePage';
import { COMMERCIAL_SEO, HOUSING_SEO, INBOX_SEO, SITES_SEO, SUPPORT_SEO, TOOL_SEO } from './seo/site';

const InboxPage = lazy(() => import('./pages/InboxPage').then((module) => ({ default: module.InboxPage })));
const SignalRadarPage = lazy(() => import('./pages/SignalRadarPage').then((module) => ({ default: module.SignalRadarPage })));
const BidRadarPage = lazy(() => import('./pages/BidRadarPage').then((module) => ({ default: module.BidRadarPage })));
const CommercialAreaRadarPage = lazy(() => import('./pages/CommercialAreaRadarPage').then((module) => ({ default: module.CommercialAreaRadarPage })));
const BuildingRegionControls = lazy(() => import('./components/BuildingRegionControls').then((module) => ({ default: module.BuildingRegionControls })));

function withSeo(seo: SeoDefinition, element: ReactNode) {
  return <><ProductSeo seo={seo} />{element}</>;
}

function lazyPage(element: ReactNode) {
  return <Suspense fallback={<p className="route-loading" role="status">화면을 불러오는 중입니다.</p>}>{element}</Suspense>;
}

export function App() {
  return (
    <>
      <AnalyticsObserver />
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<ProductHomePage />} />
          <Route path="workspace" element={<ProductHomePage />} />
          <Route path="inbox" element={withSeo(INBOX_SEO, lazyPage(<InboxPage />))} />
          <Route path="radar/housing" element={withSeo(HOUSING_SEO, lazyPage(<SignalRadarPage kind="HOUSING" />))} />
          <Route path="radar/bids" element={lazyPage(<BidRadarPage />)} />
          <Route path="radar/support" element={withSeo(SUPPORT_SEO, lazyPage(<SignalRadarPage kind="SUPPORT" />))} />
          <Route path="radar/franchises" element={<Navigate replace to="/radar/commercial-areas" />} />
          <Route path="radar/commercial-areas" element={withSeo(COMMERCIAL_SEO, lazyPage(<CommercialAreaRadarPage />))} />
          <Route path="radar/sites" element={withSeo(SITES_SEO, <><section className="legacy-radar-notice" role="status"><strong>신규현장 Radar는 기존 링크 호환용으로 유지됩니다.</strong><span>현재 주력 탐색 기능은 공고·입찰과 상권 Radar입니다.</span><div><Link to="/radar/bids">공고·입찰 보기</Link><Link to="/radar/commercial-areas">상권 보기</Link></div></section>{lazyPage(<BuildingRegionControls />)}{lazyPage(<SignalRadarPage kind="SITE" />)}</>)} />
          <Route path="tools/profitability" element={withSeo(TOOL_SEO.profitability, <DecisionStudioPage kind="profitability" />)} />
          <Route path="tools/pricing" element={withSeo(TOOL_SEO.pricing, <DecisionStudioPage kind="pricing" />)} />
          <Route path="tools/planning" element={withSeo(TOOL_SEO.planning, <DecisionStudioPage kind="planning" />)} />
          <Route path="tools/growth" element={withSeo(TOOL_SEO.growth, <DecisionStudioPage kind="growth" />)} />
          <Route path="calculators" element={<HomePage />} />
          <Route path="calculators/:slug" element={<CalculatorPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}
